import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useSettings, resolveImageUrl } from '../context/SettingsContext';
import ResponsiveImage from './ResponsiveImage';
import { isVideoUrl } from './ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color palette per badge — drives both the top accent bar AND the pill below the image.
const ACCENT_COLORS = {
  primary: { bar: 'bg-sky-700',     pill: 'bg-sky-700 text-white' },
  accent:  { bar: 'bg-yellow-400',  pill: 'bg-yellow-400 text-neutral-900' },
  orange:  { bar: 'bg-orange-500',  pill: 'bg-orange-500 text-white' },
  red:     { bar: 'bg-red-700',     pill: 'bg-red-700 text-white' },
  green:   { bar: 'bg-emerald-600', pill: 'bg-emerald-600 text-white' },
};
// Fallback colors cycle through the palette when products don't have a badge color set.
const FALLBACK_PALETTE = ['primary', 'red', 'orange', 'accent', 'green'];

const NeuheitenSection = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [startIdx, setStartIdx] = useState(0);
  const [perView, setPerView] = useState(2);
  const pauseRef = useRef(false);

  const eyebrow = settings?.home?.neuheiten_eyebrow || 'Unsere Hüpfburgen';
  const title = settings?.home?.neuheiten_title || 'Unsere Neuheiten';

  useEffect(() => {
    axios.get(`${API}/products`).then((res) => {
      // Coming-Soon-Produkte dürfen hier NICHT erscheinen — der Slider ist
      // der Neuheiten-Slot für aktuelle Highlights, nicht für Teaser.
      const visible = res.data.filter((p) => p.coming_soon !== true);
      const featured = visible.filter((p) => p.is_featured);
      setProducts(featured.length > 0 ? featured : visible.slice(0, 6));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      // Landscape cards — like the category page; 3 per row on desktop, 2 on tablet, 1 on phone.
      setPerView(w < 768 ? 1 : w < 1280 ? 2 : 3);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const total = products.length;

  useEffect(() => {
    if (total <= perView) return;
    const interval = setInterval(() => {
      if (!pauseRef.current) setStartIdx((idx) => (idx + 1) % total);
    }, 4500);
    return () => clearInterval(interval);
  }, [total, perView]);

  const next = useCallback(() => setStartIdx((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setStartIdx((i) => (i - 1 + total) % total), [total]);

  if (total === 0) return null;

  const visible = [];
  for (let i = 0; i < Math.min(perView, total); i++) {
    visible.push(products[(startIdx + i) % total]);
  }

  const gridCls =
    perView === 1
      ? 'grid-cols-1'
      : perView === 2
      ? 'grid-cols-1 md:grid-cols-2'
      : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3';

  return (
    <section
      className="py-4 md:py-8"
      data-testid="neuheiten-section"
      onMouseEnter={() => (pauseRef.current = true)}
      onMouseLeave={() => (pauseRef.current = false)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5 md:mb-7">
          <div className="space-y-2">
            <span className="block text-sm md:text-base font-bold uppercase tracking-widest text-sky-500">
              {eyebrow}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-sky-700 leading-tight max-w-3xl">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => navigate('/produkte')}
            className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-semibold whitespace-nowrap self-start md:self-end"
            data-testid="neuheiten-all-products"
          >
            Alle Produkte ansehen
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        <div className="relative">
          {total > perView && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Vorherige"
                className="hidden md:flex items-center justify-center absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 bg-white text-sky-500 hover:bg-sky-500 hover:text-white rounded-full h-12 w-12 z-20 shadow-lg border border-neutral-200 transition-colors"
                data-testid="neuheiten-prev"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Nächste"
                className="hidden md:flex items-center justify-center absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 bg-white text-sky-500 hover:bg-sky-500 hover:text-white rounded-full h-12 w-12 z-20 shadow-lg border border-neutral-200 transition-colors"
                data-testid="neuheiten-next"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          <div className={`grid ${gridCls} gap-5 md:gap-6 transition-all duration-500`}>
            {visible.map((product, vIdx) => {
              // Resolve accent color: prefer the first badge color, fall back to a rotating palette.
              const firstBadge = (product.badges && product.badges[0]) || (product.badge_text ? { text: product.badge_text, color: product.badge_color } : null);
              const colorKey = (firstBadge && ACCENT_COLORS[firstBadge.color]) ? firstBadge.color : FALLBACK_PALETTE[((startIdx + vIdx) % FALLBACK_PALETTE.length)];
              const accent = ACCENT_COLORS[colorKey] || ACCENT_COLORS.primary;
              const pillText = firstBadge?.text || '';
              return (
                <article
                  key={`${product.id}-${startIdx}`}
                  onClick={() => navigate(`/${product.slug || product.id}`)}
                  className="group cursor-pointer bg-white rounded-3xl shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
                  data-testid={`neuheit-card-${product.id}`}
                >
                  {/* Landscape image — pill badge overlays directly on the image so there is
                      no separation between the accent bar and the photo. */}
                  <div className="relative w-full aspect-[3/2] overflow-hidden bg-neutral-100">
                    {isVideoUrl(product.image_url) ? (
                      <video
                        src={resolveImageUrl(product.image_url)}
                        className="block w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        autoPlay muted loop playsInline preload="metadata"
                        data-testid={`neuheit-video-${product.id}`}
                      />
                    ) : (
                      <ResponsiveImage
                        src={product.image_url}
                        alt={product.name}
                        sizes="(min-width: 1280px) 33vw, (min-width: 768px) 50vw, 100vw"
                        className="block w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      />
                    )}
                    {pillText && (
                      <span
                        className={`absolute top-3 left-3 inline-flex items-center px-3.5 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-wide shadow-md ${accent.pill}`}
                        data-testid={`neuheit-pill-${product.id}`}
                      >
                        {pillText}
                      </span>
                    )}
                  </div>

                  {/* Body */}
                  <div className="p-4 md:p-5 flex-1 flex flex-col gap-2">
                    <h3 className="text-lg md:text-xl font-bold text-sky-700 leading-tight group-hover:text-sky-600 transition-colors line-clamp-2">
                      {product.name}
                    </h3>
                    <p className="text-sm text-neutral-600 leading-relaxed line-clamp-3">
                      {product.description}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>

          {total > perView && (
            <div className="flex justify-center mt-8 space-x-2">
              {Array.from({ length: total }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setStartIdx(idx)}
                  className={`h-2 rounded-full transition-all ${
                    idx === startIdx ? 'w-8 bg-sky-500' : 'w-2 bg-neutral-300'
                  }`}
                  aria-label={`Slide ${idx + 1}`}
                  data-testid={`neuheit-dot-${idx}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default NeuheitenSection;
